import { useCallback, useState } from 'react';

import { arrayMove } from '@ts-core/core/components/Sort';

import { useSubjects } from 'modules/subjects/hooks';
import { Exercise } from 'modules/exercises/types';

import { ExercisesSelectorProps } from './ExercisesSelector';

interface ExercisesSelectorHookInput {
  courseId?: string;
  selectedExercises?: Exercise[];
}

interface ExercisesSelectorHookOutput {
  selectorProps: ExercisesSelectorProps;
  selectedExercises: Exercise[];
}

export default function useExercisesSelector(input: ExercisesSelectorHookInput): ExercisesSelectorHookOutput {
  const { courseId, selectedExercises: initialSelectedExercises = [] } = input;
  const [selectedExercises, setSelectedExercises] = useState<Exercise[]>(initialSelectedExercises);

  const { subjects, loading } = useSubjects({ courseId });

  const isExerciseSelected = useCallback(
    (exercise: Exercise): boolean => {
      return selectedExercises.findIndex((e) => e.id === exercise.id) !== -1;
    },
    [selectedExercises]
  );

  const onAddExercise = useCallback(
    (exercise: Exercise) => {
      if (!isExerciseSelected(exercise)) {
        setSelectedExercises((state) => [...state, exercise]);
      }
    },
    [isExerciseSelected]
  );
  const onAddExercises = useCallback(
    (exercises: Exercise[]) => {
      const exercisesToAdd = exercises.filter((e) => !isExerciseSelected(e));
      setSelectedExercises((state) => [...state, ...exercisesToAdd]);
    },
    [isExerciseSelected]
  );

  const onRemoveExercise = useCallback(
    (exercise: Exercise) => {
      if (isExerciseSelected(exercise)) {
        setSelectedExercises((state) => [...state.filter((e) => e.id !== exercise.id)]);
      }
    },
    [isExerciseSelected]
  );
  const onRemoveAllExercises = useCallback(() => {
    setSelectedExercises([]);
  }, []);

  const onSortExercises = useCallback((from: number, to: number) => {
    setSelectedExercises((state) => arrayMove(state, from, to));
  }, []);

  return {
    selectorProps: {
      subjects,
      loadingSubjects: loading,
      selectedExercises,

      isExerciseSelected,
      onAddExercise,
      onAddExercises,

      onRemoveExercise,
      onRemoveAllExercises,

      onSortExercises,
    },
    selectedExercises,
  };
}
